import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';
import initialCopyData from '@/data/copy.json';
import { setNestedProperty, resolveCopyValue } from './dynamic-text-utils';

export interface DynamicTextContextType {
  getText: (path: string, fallback?: string) => string;
  updateText: (path: string, value: string) => Promise<boolean>;
  initText: (path: string, defaultValue: string) => Promise<boolean>;
}

const DynamicTextContext = createContext<DynamicTextContextType | null>(null);

export function DynamicTextProvider({ children }: { children: ReactNode }) {
  const [copyState, setCopyState] = useState<Record<string, any>>(() => {
    try {
      return JSON.parse(JSON.stringify(initialCopyData));
    } catch {
      return initialCopyData as Record<string, any>;
    }
  });

  const getText = useCallback(
    (pathStr: string, fallback?: string): string => {
      const resolved = resolveCopyValue(copyState, pathStr);
      if (resolved !== undefined && resolved !== '') return resolved;
      return fallback ?? '';
    },
    [copyState]
  );

  const updateText = useCallback(async (pathStr: string, value: string): Promise<boolean> => {
    // Optimistically update React state
    setCopyState((prev) => {
      const cloned = JSON.parse(JSON.stringify(prev));
      if (!cloned.text) cloned.text = {};
      const target = cloned.text && !pathStr.startsWith('text.') ? cloned.text : cloned;
      const key = pathStr.startsWith('text.') ? pathStr.replace(/^text\./, '') : pathStr;
      setNestedProperty(target, key, value);
      return cloned;
    });

    if (import.meta.env.DEV) {
      try {
        const res = await fetch('/__dynamic_text/update', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ path: pathStr, value }),
        });
        return res.ok;
      } catch (err) {
        console.error(`[DynamicText] Failed to persist "${pathStr}" to server:`, err);
        return false;
      }
    }
    return true;
  }, []);

  const initText = useCallback(
    async (pathStr: string, defaultValue: string): Promise<boolean> => {
      const existing = resolveCopyValue(copyState, pathStr);
      if (existing !== undefined && existing !== '') {
        return false;
      }

      setCopyState((prev) => {
        const cloned = JSON.parse(JSON.stringify(prev));
        if (!cloned.text) cloned.text = {};
        const target = cloned.text && !pathStr.startsWith('text.') ? cloned.text : cloned;
        const key = pathStr.startsWith('text.') ? pathStr.replace(/^text\./, '') : pathStr;
        setNestedProperty(target, key, defaultValue);
        return cloned;
      });

      if (import.meta.env.DEV) {
        try {
          const res = await fetch('/__dynamic_text/init', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ path: pathStr, defaultValue }),
          });
          return res.ok;
        } catch (err) {
          console.error(`[DynamicText] Failed to auto-initialize "${pathStr}":`, err);
          return false;
        }
      }
      return true;
    },
    [copyState]
  );

  return (
    <DynamicTextContext.Provider value={{ getText, updateText, initText }}>
      {children}
    </DynamicTextContext.Provider>
  );
}

export function useDynamicText(): DynamicTextContextType {
  const ctx = useContext(DynamicTextContext);
  if (!ctx) {
    // Fallback when outside provider
    return {
      getText: (pathStr: string, fallback?: string) => {
        const val = resolveCopyValue(initialCopyData, pathStr);
        return (val !== undefined && val !== '') ? val : (fallback ?? '');
      },
      updateText: async () => false,
      initText: async () => false,
    };
  }
  return ctx;
}
